import { UTCDate } from '@date-fns/utc';
import { Big } from 'big.js';
import { isSameMonth, startOfMonth, subMonths } from 'date-fns';
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { TransactionView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';

export type MonthlyAmountBreakdown = {
  currency: SupportedCountryCurrency;
  totalAmount: number;
  currentMonthAmount: number;
  previousMonthAmount: number;
  twoMonthsAgoAmount: number;
  threeMonthsAgoAmount: number;
  monthOverMonthIncrease: number;
};

/**
 * Formats month-over-month change for display in the reporting table
 * @param change - The monthOverMonthIncrease value
 * @param twoMonthsAgoAmount - The amount from two months ago (used for percentage calculation)
 * @returns Formatted MoM change with percentage, sign, and arrow
 */
export const formatMomChange = (change: number, twoMonthsAgoAmount: number) => {
  // monthOverMonthIncrease = previousMonthAmount - twoMonthsAgoAmount
  // For outgoing expenses (negative amounts):
  // - If monthOverMonthIncrease is positive: spending decreased (previous month less negative than two months ago)
  // - If monthOverMonthIncrease is negative: spending increased (previous month more negative than two months ago)
  // We need to invert the sign for display purposes
  const displayChange = -change; // Invert the sign
  const isPositive = displayChange > 0;
  const isNegative = displayChange < 0;
  const isZero = displayChange === 0;

  // Calculate percentage change from two months ago to previous month
  let percentageChange = 0;
  if (twoMonthsAgoAmount !== 0 && !isZero) {
    percentageChange = (displayChange / Math.abs(twoMonthsAgoAmount)) * 100;
  }

  return {
    percentageChange: Math.round(percentageChange),
    isPositive,
    isNegative,
    isZero,
    colorClass: isPositive ? 'text-seapoint-green' : isNegative ? 'text-seapoint-red' : 'text-seapoint-gray-text',
    arrow: isPositive ? ArrowUp : isNegative ? ArrowDown : ArrowRight,
    sign: isPositive ? '+' : '',
  };
};

export const calculateMonthlyBreakdown = (
  transactions: TransactionView[] = [],
  baseCurrency: SupportedCountryCurrency,
): MonthlyAmountBreakdown => {
  const currentMonth = startOfMonth(new UTCDate());
  const previousMonth = startOfMonth(subMonths(currentMonth, 1));
  const twoMonthsAgo = startOfMonth(subMonths(currentMonth, 2));
  const threeMonthsAgo = startOfMonth(subMonths(currentMonth, 3));

  return transactions.reduce(
    (result, t) => {
      const transactionDate = new UTCDate(t.fspDate);
      // Convert amount to negative if it's not a credit transaction
      // Important: we need to use baseCurrencyAmount in order for the reporting amounts to change when the base conversion is changed
      // TODO: migrate to getNormalizedAmount
      const amount = t.isCredit ? new Big(t.baseCurrencyAmount) : new Big(t.baseCurrencyAmount).times(-1);

      const currentMonthAmount = isSameMonth(transactionDate, currentMonth)
        ? amount.plus(result.currentMonthAmount)
        : new Big(result.currentMonthAmount);
      const previousMonthAmount = isSameMonth(transactionDate, previousMonth)
        ? amount.plus(result.previousMonthAmount)
        : new Big(result.previousMonthAmount);
      const twoMonthsAgoAmount = isSameMonth(transactionDate, twoMonthsAgo)
        ? amount.plus(result.twoMonthsAgoAmount)
        : new Big(result.twoMonthsAgoAmount);
      const threeMonthsAgoAmount = isSameMonth(transactionDate, threeMonthsAgo)
        ? amount.plus(result.threeMonthsAgoAmount)
        : new Big(result.threeMonthsAgoAmount);

      // We only show total for the last 4 months displayed in the UI
      const totalAmount = currentMonthAmount
        .plus(previousMonthAmount)
        .plus(twoMonthsAgoAmount)
        .plus(threeMonthsAgoAmount);

      // We convert the Big numbers to numbers to avoid having to use the type Big anywhere else in the app
      return {
        ...result,
        totalAmount: Number(totalAmount),
        currentMonthAmount: Number(currentMonthAmount),
        previousMonthAmount: Number(previousMonthAmount),
        twoMonthsAgoAmount: Number(twoMonthsAgoAmount),
        threeMonthsAgoAmount: Number(threeMonthsAgoAmount),
        monthOverMonthIncrease: Number(previousMonthAmount.minus(twoMonthsAgoAmount)),
      };
    },
    {
      currency: baseCurrency,
      totalAmount: 0,
      currentMonthAmount: 0,
      previousMonthAmount: 0,
      twoMonthsAgoAmount: 0,
      threeMonthsAgoAmount: 0,
      monthOverMonthIncrease: 0,
    },
  );
};
